import React from 'react';
import { cn } from '../../lib/utils';

export interface EmptyStateProps {
  /** Main title/heading */
  title: string;
  /** Optional description text */
  description?: string;
  /** Custom icon (defaults to empty box icon) */
  icon?: React.ReactNode;
  /** Action button or element */
  action?: React.ReactNode;
  /** Custom className */
  className?: string;
}

const DefaultIcon = () => (
  <svg
    className="w-16 h-16 text-gray-400"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
    />
  </svg>
);

/**
 * EmptyState component for displaying empty lists or no data
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="No campaigns yet"
 *   description="Create your first campaign to get started"
 *   action={<Button onClick={handleCreate}>Create Campaign</Button>}
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  action,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-8 text-center',
        className
      )}
    >
      {/* Icon */}
      <div className="mb-4">
        {icon || <DefaultIcon />}
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p className="text-gray-500 mb-4 max-w-sm">
          {description}
        </p>
      )}

      {/* Action */}
      {action && (
        <div className="mt-2">
          {action}
        </div>
      )}
    </div>
  );
};

EmptyState.displayName = 'EmptyState';
